'use client'

import Link from 'next/link'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { MessageCircleQuestion, UserRound } from 'lucide-react'
import { useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { cn } from '@/utilities/ui'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { isPathActive } from '@/utilities/navigation'
import SignOutIcon from '@/components/icons/SignOutIcon'

type AccountMenuProps = {
  profileHref?: string
}

const SUPPORT_CENTER_URL = 'https://support.poweredbyash.com/hc/en-us'

export default function AccountMenu({ profileHref }: Readonly<AccountMenuProps>) {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const pathname = usePathname()
  const isProfilePage = profileHref ? isPathActive(pathname, profileHref) : false

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    trackEvent('navigation:account_menu_toggle', { state: open ? 'open' : 'close' })
  }

  return (
    <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label="User profile"
          data-testid="patient-account-menu-button"
          className={cn(
            'relative inline-flex size-[40px] items-center justify-center rounded-full',
            'cursor-pointer bg-tenant-subtle-accent-background border border-tenant-primary-action-color/30',
            'hover:opacity-80 transition-opacity',
            isProfilePage &&
              "after:pointer-events-none after:absolute after:inset-px after:rounded-full after:border after:border-tenant-primary-action-color after:content-['']",
          )}
        >
          <UserRound className="size-5 text-tenant-primary-action-color" aria-hidden="true" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          sideOffset={8}
          className={cn(
            'z-50 w-56 rounded-3xl border border-tenant-border bg-tenant-card-background text-tenant-card-foreground p-2 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            const target = event.detail.originalEvent.target
            if (target instanceof Node && triggerRef.current?.contains(target)) {
              event.preventDefault()
            }
          }}
          data-testid="patient-account-dropdown"
        >
          {profileHref && (
            <Link
              href={profileHref}
              className="flex w-full items-center gap-3 rounded-full px-3 py-3 hover:bg-tenant-subtle-accent-background"
              data-testid="patient-account-dropdown-profile"
              onClick={() => trackEvent('navigation:profile_click')}
            >
              <UserRound
                className="h-5 w-5 shrink-0 text-tenant-primary-action-color"
                aria-hidden="true"
              />
              <span className="text-sm text-tenant-card-foreground">My Profile</span>
            </Link>
          )}
          <a
            href={SUPPORT_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center gap-3 rounded-full px-3 py-3 hover:bg-tenant-subtle-accent-background"
            data-testid="patient-account-dropdown-support-center"
            onClick={() => trackEvent('navigation:support_center_click')}
          >
            <MessageCircleQuestion
              className="h-5 w-5 shrink-0 text-tenant-primary-action-color"
              aria-hidden="true"
            />
            <span className="text-sm text-tenant-card-foreground">Support Center</span>
          </a>
          <Link
            href="/logout?reason=logout"
            className="mt-1 flex w-full items-center gap-3 rounded-full px-3 py-3 hover:bg-tenant-subtle-accent-background"
            data-testid="patient-account-dropdown-logout"
            onClick={() => trackEvent('logout:click')}
          >
            <SignOutIcon className="h-5 w-5 shrink-0 text-coral" aria-hidden="true" />
            <span className="text-sm text-tenant-card-foreground">Log out</span>
          </Link>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
